import React, { useState, useEffect } from 'react';
import API from '../services/api';
import '../styles/SystemMetrics.css';

const SystemMetrics = () => {
  const [system, setSystem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMetrics = async () => {
    try {
      const data = await API.getSystemMetrics();
      setSystem(data);
    } catch (error) {
      console.error('Error fetching system metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (isLoading) {
    return <div className="system-metrics-loading">Loading metrics...</div>;
  }

  return (
    <div className="system-metrics-section">
      <div className="section-header">
        <h2>System Metrics</h2>
        <span className="live-indicator">
          <span className="live-dot"></span>
          LIVE
        </span>
      </div>

      <div className="system-grid">
        {/* CPU Usage Card */}
        <div className="system-card">
          <div className="system-header">
            <span className="system-label">CPU Usage</span>
            <span className="system-value">{isLoading ? '...' : `${system?.cpu.usage || 0}%`}</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill cpu"
              style={{ width: `${system?.cpu.usage || 0}%` }}
            />
          </div>
          <span className="system-detail">{system?.cpu.cores || 0} cores</span>
        </div>

        {/* Memory Card */}
        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Memory</span>
            <span className="system-value">
              {isLoading ? '...' : `${system?.memory.usagePercent || 0}%`}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill memory"
              style={{ width: `${system?.memory.usagePercent || 0}%` }}
            />
          </div>
          <span className="system-detail">
            {system ? `${system.memory.used} MB / ${system.memory.total} MB` : '...'}
          </span>
        </div>

        {/* Disk Card */}
        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Disk</span>
            <span className="system-value">
              {isLoading ? '...' : `${system?.disk.usagePercent || 0}%`}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill disk"
              style={{ width: `${system?.disk.usagePercent || 0}%` }}
            />
          </div>
          <span className="system-detail">
            {system ? `${system.disk.used} GB / ${system.disk.total} GB` : '...'}
          </span>
        </div>

        {/* Network I/O Card */}
        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Network I/O</span>
            <span className="system-value uptime">
              {isLoading ? '...' : formatUptime(system?.uptime || 0)}
            </span>
          </div>
          <div className="network-stats">
            <div className="network-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
              <span>{system?.network.transmitted || 0} MB</span>
            </div>
            <div className="network-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
              <span>{system?.network.received || 0} MB</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMetrics;
